import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { styled, useTheme } from '@mui/material/styles';
import Grid from '@mui/material/Grid';
import pickBy from 'lodash/fp/pickBy';
import isNil from 'lodash/fp/isNil';
import groupBy from 'lodash/fp/groupBy';
import isEmpty from 'lodash/fp/isEmpty';
import { startOfDay } from 'date-fns/startOfDay';
import { isAfter } from 'date-fns/isAfter';
import { isSameDay } from 'date-fns/isSameDay';
import { useNavigate, useParams } from 'react-router-dom';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

import { Modal, Title, HrefLink } from '@bloomays-lib/ui.shared';
import {
  ApolloGqlJobFunnel,
  ApolloGqlTalentForTalentBoard,
  ApolloGqlTalentBoardStats,
  TalentBoardJobFunnelPhase,
} from '@bloomays-lib/types.shared';
import { useAuthStore } from '@bloomays-lib/common.auth/browser';
import { unaccent } from '@bloomays-lib/utils.shared';

import JobFunnelColumn from '../molecules/JobFunnelColumn';
import useTalentBoardStore from '../../stores/talentBoardStore';
import useTalentBoardFiltersStore from '../../stores/talentBoardFiltersStore';
import TalentJob from './TalentJob';
import EmptyTalentBoardHeader from './EmptyTalentBoardHeader';
import TalentBoardHeader from './TalentBoardHeader';

type TalentsByPhase = {
  qualificationTalents: ApolloGqlTalentForTalentBoard[];
  positionnementTalents: ApolloGqlTalentForTalentBoard[];
  itwClientTalents: ApolloGqlTalentForTalentBoard[];
  notValidatedTalents: ApolloGqlTalentForTalentBoard[];
};

const buildTalentsByPhase = (talentsRecord: Record<string, ApolloGqlTalentForTalentBoard> | null): TalentsByPhase => {
  const groupedTalents = groupBy('phase', talentsRecord || {});

  return {
    qualificationTalents: groupedTalents[TalentBoardJobFunnelPhase.Qualification] || [],
    positionnementTalents: groupedTalents[TalentBoardJobFunnelPhase.Positionnement] || [],
    itwClientTalents: groupedTalents[TalentBoardJobFunnelPhase.ITWClient] || [],
    notValidatedTalents: groupedTalents[TalentBoardJobFunnelPhase.NotValidated] || [],
  };
};

const matchesDateFilter = (talent: ApolloGqlTalentForTalentBoard, addedAfterDate: Date | null): boolean => {
  if (!addedAfterDate) {
    return true;
  }
  if (isNil(talent.addedInProject)) {
    return false;
  }
  const talentDate = startOfDay(talent.addedInProject);
  const filterDate = startOfDay(addedAfterDate);
  return isSameDay(talent.addedInProject, addedAfterDate) || isAfter(talentDate, filterDate);
};

const filterTalentBoardTalents = (
  talentsToFilter: Record<string, ApolloGqlTalentForTalentBoard> | null,
  topProfileFilter: boolean,
  notMarkedAsSeenFilter: boolean,
  searchQuery: string,
  addedAfterDate: Date | null,
): Record<string, ApolloGqlTalentForTalentBoard> | null => {
  const shouldFilterTalents =
    topProfileFilter || notMarkedAsSeenFilter || Boolean(searchQuery) || Boolean(addedAfterDate);

  if (!talentsToFilter || !shouldFilterTalents) {
    return talentsToFilter;
  }

  return pickBy((talent) => {
    const matchesTopProfile = !topProfileFilter || talent.topProfile;
    const matchesNotMarkedAsSeen = !notMarkedAsSeenFilter || !talent.markedAsSeen;
    const matchesSearch =
      !searchQuery ||
      unaccent(`${talent.firstname} ${talent.lastname}`).toLowerCase().includes(unaccent(searchQuery).toLowerCase());
    const matchesDate = matchesDateFilter(talent, addedAfterDate);

    return matchesTopProfile && matchesNotMarkedAsSeen && matchesSearch && matchesDate;
  }, talentsToFilter);
};

export type TalentBoardProps = {
  job: ApolloGqlJobFunnel;
  talents: Record<string, ApolloGqlTalentForTalentBoard> | null;
  stats: ApolloGqlTalentBoardStats | null;
  loading: boolean;
};

const TalentBoard = ({ job, talents, stats, loading }: TalentBoardProps) => {
  const theme = useTheme();
  const { t } = useTranslation('talentBoard');
  const navigate = useNavigate();
  const { talentId } = useParams<{ talentId?: string }>();
  const auth = useAuthStore((state) => state.auth);
  const isBloomie = (auth?.user?.email || '').includes('@bloomays.com') || false;

  const {
    isExpanded: isSideBarOpen,
    modalOpen,
    setModalOpen,
    setSelectedTalent,
    previousJobId,
    setPreviousJobId,
    setModalTab,
    setModalContentTab,
  } = useTalentBoardStore();
  const { topProfileFilter, notMarkedAsSeenFilter, searchQuery, addedAfterDate, resetFilters } =
    useTalentBoardFiltersStore();

  const talentsByPhase = buildTalentsByPhase(talents);
  const haveFilters = topProfileFilter || notMarkedAsSeenFilter || Boolean(searchQuery) || Boolean(addedAfterDate);
  const filteredTalents = filterTalentBoardTalents(
    talents,
    topProfileFilter,
    notMarkedAsSeenFilter,
    searchQuery,
    addedAfterDate,
  );
  const filteredTalentsByPhase = buildTalentsByPhase(filteredTalents);

  useEffect(() => {
    if (previousJobId !== null && previousJobId !== job.id) {
      setModalOpen(false);
      setSelectedTalent(null);
      resetFilters();
    }
    setPreviousJobId(job.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job.id]);

  const handleTalentClick = (talent: ApolloGqlTalentForTalentBoard) => {
    setSelectedTalent(talent);
    setModalOpen(true);
    setModalTab('notes');
    setModalContentTab('experience');
    void navigate(`/talentboard/jobs/${job.id}/talent/${talent.id}`);
  };

  useEffect(() => {
    if (!talents || !talentId) {
      return;
    }

    const matchingTalent = talents[talentId];
    if (!matchingTalent) {
      return;
    }

    setSelectedTalent(matchingTalent);
    setModalOpen(true);
  }, [talents, talentId, setSelectedTalent, setModalOpen]);

  useEffect(() => {
    if (!talentId && modalOpen) {
      setModalOpen(false);
      setSelectedTalent(null);
    }
  }, [talentId, modalOpen, setModalOpen, setSelectedTalent]);

  const closeModal = () => {
    setModalOpen(false);
    setSelectedTalent(null);
    void navigate(`/talentboard/jobs/${job.id}`);
  };

  if (loading || talents === null) {
    return (
      <Container>
        <Title textTitle={job.name} variant="heading5" sx={{ textAlign: 'left' }} />
        <TalentBoardHeader
          loading={true}
          job={job}
          talentsData={{
            notValidatedTalentsLength: 0,
            qualificationTalentsLength: 0,
            positionnementTalentsLength: 0,
            itwClientTalentsLength: 0,
          }}
          stats={null}
        />
      </Container>
    );
  }

  return (
    <Container>
      <Modal
        open={modalOpen}
        onClose={() => closeModal()}
        additionnalCSS={{
          padding: '0px',
          width: 'calc(100vw - 285px)',
          height: '100vh',
          right: '0',
          top: '0',
          left: 'auto',
          transform: 'none',
          [theme.breakpoints.down('lg')]: {
            width: '90vw',
          },
          [theme.breakpoints.down('md')]: {
            backgroundColor: theme.palette.neutral[10],
            overflowY: 'auto',
            width: '100vw',
          },
        }}
        additionnalCSSCross={{
          float: 'left',
          position: 'absolute',
          top: '24px',
          left: '24px',
          zIndex: '1000',
          [theme.breakpoints.down('md')]: {
            top: '30px',
            left: '12px',
          },
        }}
      >
        <TalentJob jobName={job.name} jobId={job.id} />
      </Modal>
      <TalentBoardHeaderContainer>
        <Title textTitle={job.name} variant="heading5" sx={{ textAlign: 'left' }} />
        {isBloomie && (
          <BloomieLinksContainer>
            <OpenInNewIcon />
            {t('headerLinks.openInNew')}
            <HrefLink
              href={`${import.meta.env.VITE_HIRESWEET_FRONT_URL}/reveal/projects/recruiting/${
                job.hiresweetId
              }/profiles`}
              target="_blank"
              sx={{
                color: theme.palette.neutral.black,
                '& .MuiTypography-root': { color: theme.palette.neutral.black },
              }}
            >
              {t('headerLinks.ats')}
            </HrefLink>
            <HrefLink
              href={`https://app-eu1.hubspot.com/contacts/139539088/record/0-3/${job.hubspotDealId}`}
              target="_blank"
              sx={{
                color: theme.palette.neutral.black,
                '& .MuiTypography-root': { color: theme.palette.neutral.black },
              }}
            >
              {t('headerLinks.crm')}
            </HrefLink>
            <HrefLink
              href={`${import.meta.env.VITE_PUBLIC_APP_ENDPOINT}jobs/${job.id}`}
              target="_blank"
              sx={{
                color: theme.palette.neutral.black,
                '& .MuiTypography-root': { color: theme.palette.neutral.black },
              }}
            >
              {t('headerLinks.app')}
            </HrefLink>
          </BloomieLinksContainer>
        )}
      </TalentBoardHeaderContainer>
      {!talents || isEmpty(talents) ? (
        <EmptyTalentBoardHeader />
      ) : (
        <TalentBoardHeader
          loading={loading}
          job={job}
          talentsData={{
            notValidatedTalentsLength: talentsByPhase.notValidatedTalents.length,
            qualificationTalentsLength: talentsByPhase.qualificationTalents.length,
            positionnementTalentsLength: talentsByPhase.positionnementTalents.length,
            itwClientTalentsLength: talentsByPhase.itwClientTalents.length,
          }}
          stats={stats}
        />
      )}
      <TalentBoardColumns>
        <Grid container spacing={1}>
          <Grid size={{ xs: 12, md: isSideBarOpen ? 12 : 3, lg: 3 }}>
            <JobFunnelColumn
              jobId={job.id}
              jobName={job.name}
              phase={TalentBoardJobFunnelPhase.Qualification}
              talents={filteredTalentsByPhase.qualificationTalents}
              onTalentClick={handleTalentClick}
              haveFilters={haveFilters}
              onResetFilters={resetFilters}
            />
          </Grid>
          <Grid size={{ xs: 12, md: isSideBarOpen ? 12 : 3, lg: 3 }}>
            <JobFunnelColumn
              jobId={job.id}
              jobName={job.name}
              phase={TalentBoardJobFunnelPhase.Positionnement}
              talents={filteredTalentsByPhase.positionnementTalents}
              onTalentClick={handleTalentClick}
              haveFilters={haveFilters}
              onResetFilters={resetFilters}
            />
          </Grid>
          <Grid size={{ xs: 12, md: isSideBarOpen ? 12 : 3, lg: 3 }}>
            <JobFunnelColumn
              jobId={job.id}
              jobName={job.name}
              phase={TalentBoardJobFunnelPhase.ITWClient}
              talents={filteredTalentsByPhase.itwClientTalents}
              onTalentClick={handleTalentClick}
              haveFilters={haveFilters}
              onResetFilters={resetFilters}
            />
          </Grid>
          <Grid size={{ xs: 12, md: isSideBarOpen ? 12 : 3, lg: 3 }}>
            <JobFunnelColumn
              jobId={job.id}
              jobName={job.name}
              phase={TalentBoardJobFunnelPhase.NotValidated}
              talents={filteredTalentsByPhase.notValidatedTalents}
              onTalentClick={handleTalentClick}
              haveFilters={haveFilters}
              onResetFilters={resetFilters}
            />
          </Grid>
        </Grid>
        {(!talents || isEmpty(talents)) && (
          <div>
            <Title textTitle={t('emptyTalentBoard.title')} variant="heading6" sx={{ textAlign: 'center' }} />
            <EmptyDescription>{t('emptyTalentBoard.description')}</EmptyDescription>
          </div>
        )}
      </TalentBoardColumns>
    </Container>
  );
};

const Container = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '24px',

  [theme.breakpoints.down('md')]: {
    gap: '8px',
  },
}));

const TalentBoardHeaderContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '16px',

  [theme.breakpoints.down('md')]: {
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: '8px',
  },
}));

const BloomieLinksContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  gap: '16px',
  alignItems: 'center',
  width: '100%',
  justifyContent: 'flex-end',
  fontSize: '14px',

  [theme.breakpoints.down('lg')]: {
    display: 'none',
  },
}));

const TalentBoardColumns = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '24px',
  [theme.breakpoints.down('md')]: {
    flexDirection: 'column-reverse',
  },
}));

const EmptyDescription = styled('p')(({ theme }) => ({
  fontSize: '16px',
  lineHeight: '22.4px',
  textAlign: 'center',
  fontFamily: '"Poppins Medium", sans-serif',
}));

export default TalentBoard;
